import { useId, type CSSProperties, type ReactNode } from 'react'
import { Link, useParams } from 'react-router'
import { IconArrowLeft, Spinner, Text, VisuallyHidden } from '@n26/nxd'
import type { CardStub } from '@/features/dashboard/api/schemas'
import { ROUTES } from '@/shared/routes'
import { formatLike, toFigure } from '@/features/dashboard/format/figure'
import { sentenceCase } from '@/features/dashboard/format/format'
import { useDashboardStructure } from '@/features/dashboard/api/queries'
import { DeepDiveFilters } from '@/features/dashboard/filters/DeepDiveFilters'
import { useDeepDiveCard } from '@/features/dashboard/filters/useDeepDiveCard'
import { DeepDiveChart } from '@/features/dashboard/charts/DeepDiveChart'
import { deepDiveKind } from '@/features/dashboard/charts/lib/deepDiveOptions'
import { EmptyCardMessage } from '@/features/dashboard/tiles/EmptyCardMessage'
import { MarketFilterNote } from '@/features/dashboard/tiles/MarketFilterNote'
import { CardFigure } from '@/features/dashboard/tiles/CardFigure'
import { InfoTip } from '@/shared/ui/InfoTip'
import {
  computeBlockSpans,
  deepDiveGroups,
  splitKpis,
  type DeepDiveGroup,
} from './deepDiveLayout'
import styles from './DeepDivePage.module.css'

const Loading = () => (
  <div role='status'>
    <Spinner />
    <VisuallyHidden>Loading</VisuallyHidden>
  </div>
)

type Heading = 'h2' | 'h3'

type DeepDiveCardProps = {
  stub: CardStub
  /** The deep dive the card is drawn in, whose settings for it apply. */
  deepDiveId: string
  /** The level of the card's title, below the page's and the subsection's headings. */
  heading: Heading
  /** Lays the card out in its group's grid. */
  className?: string
  style?: CSSProperties
}

const SIZE_CLASSES = {
  'single-metric': styles.singleMetric,
  wide: styles.wide,
  'half-wide': styles.halfWide,
  half: styles.half,
}

const CardTitle = ({
  stub,
  heading: Level,
}: Pick<DeepDiveCardProps, 'stub' | 'heading'>) => (
  <div className={styles.titleRow}>
    <Level className={styles.cardTitle}>{stub.name}</Level>
    {stub.description && (
      <InfoTip name={stub.name} description={stub.description} />
    )}
  </div>
)

const DeepDiveCard = ({
  stub,
  deepDiveId,
  heading,
  className = '',
  style,
}: DeepDiveCardProps) => {
  const {
    data: card,
    isPending,
    isError,
    clearMarkets,
  } = useDeepDiveCard(stub, deepDiveId)
  const size = stub.layout?.size
  const sized = `${className} ${size ? SIZE_CLASSES[size] : ''}`

  const frame = (extra: string, children: ReactNode, label?: string) => (
    <section
      className={`${styles.chart} ${extra} ${sized}`}
      style={style}
      aria-label={label}
    >
      {children}
    </section>
  )

  if (isPending) return frame('', <Loading />)
  if (isError) {
    return (
      <div className={`${styles.chart} ${sized}`} style={style} role='alert'>
        <Text color='rhubarb'>Couldn&apos;t load this card.</Text>
      </div>
    )
  }

  if (card.kind === 'kpi') {
    const figure = toFigure(card)
    return (
      <section
        className={`${styles.kpi} ${sized}`}
        style={style}
        aria-label={card.name}
      >
        <CardTitle stub={stub} heading={heading} />
        {figure && (
          <CardFigure
            figure={figure}
            size='expanded'
            className={styles.kpiValues}
          />
        )}
        {figure && figure.previous !== null && (
          <p className={styles.previous}>
            Previous: {formatLike(figure, figure.previous)}
          </p>
        )}
      </section>
    )
  }

  const title = <CardTitle stub={stub} heading={heading} />
  if (card.kind === 'empty') {
    return frame(
      '',
      <>
        {title}
        <EmptyCardMessage reason={card.reason} onClear={clearMarkets} />
      </>,
      card.name
    )
  }

  const isMap = deepDiveKind(card, stub.chart) === 'map'
  return frame(
    isMap ? (styles.map ?? '') : '',
    <>
      {title}
      {card.marketFiltered === false && <MarketFilterNote />}
      <DeepDiveChart card={card} chart={stub.chart} />
    </>,
    card.name
  )
}

type GroupProps = {
  group: DeepDiveGroup
  deepDiveId: string
}

/** A group of cards on its grid: see `DeepDiveGroup`. */
const CardGroup = ({ group, deepDiveId }: GroupProps) => {
  const headingId = useId()
  // Under a heading, cards are titled a level below it.
  const heading: Heading = group.heading ? 'h3' : 'h2'
  // A deep dive may list a card twice, so a card is keyed by where it sits.
  const card = (
    stub: CardStub,
    index: number,
    props: Partial<DeepDiveCardProps> = {}
  ) => (
    <DeepDiveCard
      key={`${index}-${stub.cardId}`}
      stub={stub}
      deepDiveId={deepDiveId}
      heading={heading}
      {...props}
    />
  )

  let grid: ReactNode
  if (group.layout === 'blocks') {
    // Equal tracks; a card's size is only its blocks.
    const spans = computeBlockSpans(group.cards, group.divisions)
    grid = (
      <div
        className={styles.blocks}
        style={{ gridTemplateColumns: `repeat(${group.divisions}, 1fr)` }}
      >
        {group.cards.map((stub, i) =>
          card(stub, i, { style: { gridColumn: `span ${spans[i]}` } })
        )}
      </div>
    )
  } else if (group.layout === 'grid') {
    grid = (
      <div className={styles.grid}>
        {group.cards.map((stub, i) => card(stub, i))}
      </div>
    )
  } else {
    const { kpis, charts } = splitKpis(group.cards)
    grid = (
      <>
        {kpis.length > 0 && (
          <div className={styles.kpis}>
            {kpis.map((stub, i) => card(stub, i))}
          </div>
        )}
        <div className={styles.grid}>
          {charts.map((stub, i) => card(stub, kpis.length + i))}
        </div>
      </>
    )
  }

  return (
    <div
      className={styles.group}
      role={group.heading ? 'group' : undefined}
      aria-labelledby={group.heading ? headingId : undefined}
    >
      {group.heading && (
        <h2 id={headingId} className={styles.groupHeading}>
          {group.heading}
        </h2>
      )}
      {grid}
    </div>
  )
}

export const DeepDivePage = () => {
  const { sectionId } = useParams()
  const { data: dashboard, isPending, isError } = useDashboardStructure()

  if (isPending) return <Loading />
  if (isError) {
    return (
      <div role='alert'>
        <Text color='rhubarb'>Couldn&apos;t load the dashboard.</Text>
      </div>
    )
  }

  const section = dashboard.sections.find(
    candidate => candidate.id === sectionId && candidate.kind === 'deep_dive'
  )
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Link
          to={ROUTES.HOME}
          className={styles.back}
          aria-label='Back to dashboard'
        >
          <IconArrowLeft aria-hidden />
        </Link>
        <h1 className={styles.title}>
          {section ? sentenceCase(section.title) : 'Deep dive'}
        </h1>
      </div>
      {section ? (
        <>
          <DeepDiveFilters section={section} />
          {deepDiveGroups(section).map((group, index) => (
            <CardGroup
              key={`${index}-${group.key}`}
              group={group}
              deepDiveId={section.id}
            />
          ))}
        </>
      ) : (
        <Text>Deep dive not found.</Text>
      )}
    </div>
  )
}
